import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Star, Image as ImageIcon, Upload, X, AlertCircle, Sparkles } from 'lucide-react';
import { adminApi, formatApiError, resolveImageUrl, PRODUCT_TYPES, PRODUCT_STATUSES } from '../../utils/adminApi';
import { extractFromDescription } from '../../utils/descriptionExtractor';

const TYPE_LABEL = { archive: 'Archive', 'ready-to-wear': 'Ready-to-Wear', suit: 'Suit' };

const ProductsTab = ({ onToast }) => {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null = closed, {} = create, {...} = edit

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.listProducts(filter === 'all' ? undefined : filter);
      setItems(data);
    } catch (err) {
      setError(formatApiError(err, 'Could not load products.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [filter]);

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await adminApi.deleteProduct(p.id);
      onToast?.('Product removed');
      refresh();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleToggleFeatured = async (p) => {
    try {
      await adminApi.updateProduct(p.id, { featured: !p.featured });
      refresh();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const handleSyncAll = async () => {
    const confirmation = window.confirm(
      'Auto-sync Fabric and Embroidery for ALL products?\n\n' +
      'This will ONLY fill in fields that are currently EMPTY by reading each product\u2019s description. Existing values will never be overwritten.'
    );
    if (!confirmation) return;

    try {
      setError('');
      const all = await adminApi.listProducts();
      let updated = 0;
      let skipped = 0;
      for (const p of all) {
        const { fabric, embroidery } = extractFromDescription(p.description);
        const patch = {};
        if (fabric && !(p.fabric || '').trim()) patch.fabric = fabric;
        if (embroidery && !(p.embroidery || '').trim()) patch.embroidery = embroidery;
        if (!Object.keys(patch).length) { skipped += 1; continue; }
        // eslint-disable-next-line no-await-in-loop
        await adminApi.updateProduct(p.id, patch);
        updated += 1;
      }
      onToast?.(`Auto-sync complete \u2014 ${updated} updated, ${skipped} unchanged`);
      refresh();
    } catch (err) {
      setError(formatApiError(err, 'Auto-sync failed.'));
    }
  };

  return (
    <div data-testid="products-tab">
      <div className="admin-section-actions" style={{ justifyContent: 'space-between' }}>
        <div className="admin-filter-group">
          {[
            { id: 'all', label: 'All' },
            { id: 'archive', label: 'Archive' },
            { id: 'suit', label: 'Suit' },
            { id: 'ready-to-wear', label: 'Ready-to-Wear' },
          ].map((f) => (
            <button
              key={f.id}
              className={`admin-filter-pill ${filter === f.id ? 'active' : ''}`}
              onClick={() => setFilter(f.id)}
              data-testid={`filter-${f.id}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            className="btn-secondary"
            onClick={handleSyncAll}
            data-testid="product-sync-all-btn"
            title="Read every product's description and auto-fill its Fabric and Embroidery fields"
          >
            <Sparkles size={14} /> Auto-sync All
          </button>
          <button className="btn-primary" onClick={() => setEditing({})} data-testid="product-create-btn">
            <Plus size={14} /> New Product
          </button>
        </div>
      </div>

      {error && <div className="admin-error"><AlertCircle size={14} /> {error}</div>}
      {loading && <p className="body-regular">Loading…</p>}

      {!loading && !items.length && (
        <p className="body-regular admin-empty">No products yet. Add your first commission piece or ready-to-wear creation.</p>
      )}

      {!loading && items.length > 0 && (
        <div className="admin-product-grid" data-testid="products-grid">
          {items.map((p) => (
            <article key={p.id} className="admin-product-card" data-testid={`product-card-${p.id}`}>
              <div className="admin-product-img">
                {(p.images?.[0] || p.image) ? (
                  <img src={resolveImageUrl(p.images?.[0] || p.image)} alt={p.name} />
                ) : (
                  <div className="admin-product-img-placeholder"><ImageIcon size={32} /></div>
                )}
                <button
                  className={`admin-product-fav ${p.featured ? 'on' : ''}`}
                  onClick={() => handleToggleFeatured(p)}
                  title={p.featured ? 'Featured' : 'Mark featured'}
                  data-testid={`product-featured-${p.id}`}
                >
                  <Star size={14} fill={p.featured ? 'currentColor' : 'none'} />
                </button>
              </div>
              <div className="admin-product-body">
                <div className="admin-product-meta-row">
                  <span className="admin-pill admin-pill-type">{TYPE_LABEL[p.type] || p.type}</span>
                  <span className={`admin-pill admin-pill-status admin-pill-${p.status.replace(/[^a-z]/gi, '').toLowerCase()}`}>{p.status}</span>
                </div>
                <h3 className="heading-3" style={{ marginTop: 10 }}>{p.name}</h3>
                {p.price != null && (
                  <p className="body-regular" style={{ color: 'var(--accent-gold)', marginTop: 4 }}>
                    ₹{Number(p.price).toLocaleString('en-IN')}
                  </p>
                )}
                <p className="body-small admin-product-desc">{p.description}</p>
                {(p.fabric || p.embroidery) && (
                  <p className="body-small" style={{ color: 'var(--text-secondary)', marginTop: 8 }}>
                    {[p.fabric, p.embroidery].filter(Boolean).join(' • ')}
                  </p>
                )}
                <div className="admin-product-actions">
                  <button className="btn-secondary admin-row-btn" onClick={() => setEditing(p)} data-testid={`product-edit-${p.id}`}>
                    <Pencil size={12} /> Edit
                  </button>
                  <button className="admin-icon-only" onClick={() => handleDelete(p)} title="Delete" data-testid={`product-delete-${p.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <ProductFormModal
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onToast?.(editing.id ? 'Product updated' : 'Product created');
            refresh();
          }}
        />
      )}
    </div>
  );
};

const ProductFormModal = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial.id;
  const initialImages = (initial.images && initial.images.length)
    ? initial.images
    : (initial.image ? [initial.image] : []);
  const [form, setForm] = useState({
    name: initial.name || '',
    type: initial.type || 'ready-to-wear',
    description: initial.description || '',
    images: initialImages,
    price: initial.price ?? '',
    fabric: initial.fabric || '',
    embroidery: initial.embroidery || '',
    origin: initial.origin || '',
    status: initial.status || 'Available',
    featured: initial.featured || false,
    disclaimer: initial.disclaimer || '',
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const update = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm({ ...form, [k]: v });
  };

  const handleFile = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setErr('');
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const { url } = await adminApi.uploadImage(f);
        uploaded.push(url);
      }
      setForm((f) => ({ ...f, images: [...f.images, ...uploaded] }));
    } catch (e2) {
      setErr(formatApiError(e2, 'Upload failed.'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const moveImage = (idx, delta) => {
    setForm((f) => {
      const next = [...f.images];
      const target = idx + delta;
      if (target < 0 || target >= next.length) return f;
      [next[idx], next[target]] = [next[target], next[idx]];
      return { ...f, images: next };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    setSubmitting(true);
    try {
      const payload = {
        name: form.name,
        type: form.type,
        description: form.description,
        image: form.images[0] || '',
        images: form.images,
        price: form.type === 'ready-to-wear' && form.price !== '' ? Number(form.price) : null,
        fabric: form.fabric || null,
        embroidery: form.embroidery || null,
        origin: form.origin || null,
        status: form.status,
        featured: form.featured,
        disclaimer: form.disclaimer || null,
      };
      if (isEdit) {
        await adminApi.updateProduct(initial.id, payload);
      } else {
        await adminApi.createProduct(payload);
      }
      onSaved();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="admin-modal-overlay" onClick={onClose}>
      <div className="admin-modal" onClick={(e) => e.stopPropagation()} data-testid="product-modal">
        <button className="admin-modal-close" onClick={onClose}><X size={20} /></button>
        <h2 className="heading-2" style={{ marginBottom: 24 }}>
          {isEdit ? 'Edit Product' : 'New Product'}
        </h2>

        <form onSubmit={submit} className="admin-form">
          {/* Multi-image gallery uploader */}
          <div className="admin-image-uploader admin-multi-uploader">
            <div className="admin-multi-header">
              <span className="admin-field-label">Product images</span>
              <span className="admin-multi-hint">First image is the cover. Drag-rearrange with arrows.</span>
            </div>
            <div className="admin-multi-grid">
              {form.images.map((img, idx) => (
                <div key={`${img}-${idx}`} className="admin-multi-tile" data-testid={`product-image-tile-${idx}`}>
                  <img src={resolveImageUrl(img)} alt={`product ${idx + 1}`} />
                  {idx === 0 && <span className="admin-multi-badge">Cover</span>}
                  <div className="admin-multi-actions">
                    <button type="button" onClick={() => moveImage(idx, -1)} title="Move left" disabled={idx === 0}>‹</button>
                    <button type="button" onClick={() => moveImage(idx, 1)} title="Move right" disabled={idx === form.images.length - 1}>›</button>
                    <button type="button" onClick={() => removeImage(idx)} title="Remove" data-testid={`product-image-remove-${idx}`}><X size={12} /></button>
                  </div>
                </div>
              ))}
              <label className="admin-multi-add">
                <Upload size={20} />
                <span>{uploading ? 'Uploading…' : 'Add images'}</span>
                <input type="file" accept="image/jpeg,image/png" multiple onChange={handleFile} disabled={uploading} hidden data-testid="product-image-input" />
              </label>
            </div>
            {form.images.length === 0 && (
              <div className="admin-multi-empty"><ImageIcon size={28} /> No images yet — add at least one.</div>
            )}
          </div>

          <div className="admin-form-grid">
            <label className="admin-field">
              <span className="admin-field-label">Name</span>
              <input required value={form.name} onChange={update('name')} data-testid="product-input-name" />
            </label>
            <label className="admin-field">
              <span className="admin-field-label">Type</span>
              <select value={form.type} onChange={update('type')} data-testid="product-input-type">
                {PRODUCT_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
              </select>
            </label>
            <label className="admin-field">
              <span className="admin-field-label">Status</span>
              <select value={form.status} onChange={update('status')} data-testid="product-input-status">
                {PRODUCT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            {form.type === 'ready-to-wear' && (
              <label className="admin-field">
                <span className="admin-field-label">Price (₹)</span>
                <input type="number" min="0" step="100" value={form.price} onChange={update('price')} data-testid="product-input-price" />
              </label>
            )}
            <label className="admin-field">
              <span className="admin-field-label">Fabric</span>
              <input value={form.fabric} onChange={update('fabric')} placeholder="Pure Silk" data-testid="product-input-fabric" />
            </label>
            <label className="admin-field">
              <span className="admin-field-label">Embroidery / Technique</span>
              <input value={form.embroidery} onChange={update('embroidery')} placeholder="Zardozi" data-testid="product-input-embroidery" />
            </label>
            <label className="admin-field">
              <span className="admin-field-label">Origin</span>
              <input value={form.origin} onChange={update('origin')} placeholder="Lucknow" data-testid="product-input-origin" />
            </label>
            <label className="admin-field admin-checkbox-field">
              <input type="checkbox" checked={form.featured} onChange={update('featured')} data-testid="product-input-featured" />
              <span>Featured (shows on home Collection)</span>
            </label>
          </div>

          <label className="admin-field">
            <span className="admin-field-label" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
              <span>Description</span>
              <button
                type="button"
                onClick={() => {
                  const { fabric, embroidery } = extractFromDescription(form.description);
                  const fabricEmpty = !(form.fabric || '').trim();
                  const embEmpty = !(form.embroidery || '').trim();
                  const willFill = (fabric && fabricEmpty) || (embroidery && embEmpty);
                  if (!willFill) {
                    setErr(
                      fabric || embroidery
                        ? 'Fabric and Embroidery already have values \u2014 auto-fill never overwrites. Clear the field first if you want to refill it.'
                        : 'No fabric or embroidery keywords detected in the description yet.'
                    );
                    return;
                  }
                  setErr('');
                  setForm((f) => ({
                    ...f,
                    fabric: fabricEmpty && fabric ? fabric : f.fabric,
                    embroidery: embEmpty && embroidery ? embroidery : f.embroidery,
                  }));
                }}
                className="admin-inline-btn"
                data-testid="product-autofill-btn"
                title="Fill empty Fabric / Embroidery fields by reading the description above. Never overwrites existing values."
              >
                <Sparkles size={12} /> Auto-fill empty fields
              </button>
            </span>
            <textarea rows="6" value={form.description} onChange={update('description')} data-testid="product-input-description" />
            <span className="admin-field-hint">
              Tip: write each garment piece (SHIRT, DUPATTA, GHARARA, KURTA, LEHENGA, BLOUSE, etc.) in capital letters where it begins — the website will automatically format your description into clean labelled sections for shoppers.
            </span>
          </label>

          <label className="admin-field">
            <span className="admin-field-label">Disclaimer <em style={{ fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 8 }}>(optional — shows below description on the product page)</em></span>
            <textarea
              rows="3"
              value={form.disclaimer}
              onChange={update('disclaimer')}
              placeholder="e.g. Hand-embroidery may show subtle variations from the photograph. Custom sizing welcome."
              data-testid="product-input-disclaimer"
            />
            <span className="admin-field-hint">
              Leave blank to hide the disclaimer section entirely. Use it for piece-specific notes (e.g. fabric variation, made-to-order timelines, sizing notes).
            </span>
          </label>

          {err && <div className="admin-error"><AlertCircle size={14} /> {err}</div>}

          <div className="admin-modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn-primary" disabled={submitting || uploading} data-testid="product-submit">
              {submitting ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Product'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProductsTab;
